import { Component } from 'preact';

import DailySummary from '../../components/daily-summary';
import Card from '../../components/card';
import {
	ABORT_ERROR_NAME,
	abortRequests,
	fetchDailyExtremes,
	fetchLiveData,
	fetchObservationsHourly, fetchTodayHistory
} from '../../utils/api';
import { extractDailyExtremes } from '../../utils/live-data';
import LiveUpdatedTime from '../../components/live-updated-time';
import {
	todayHistoryLoading,
	dailyObservationsLoadFailure,
	dailyObservationsLoading,
	dailyObservationsLoadSuccess,
	hourlyObservationsLoadFailure,
	hourlyObservationsLoading,
	hourlyObservationsLoadSuccess,
	initialState,
	liveDataLoadFailure,
	liveDataLoading,
	liveDataLoadSuccess,
	OBSERVATIONS_VIEW,
	pauseLiveUpdates,
	resumeLiveUpdates,
	switchObservationsView,
	yesterdayExtremesLoadFailure,
	yesterdayExtremesLoading,
	yesterdayExtremesLoadSuccess, todayHistoryLoadSuccess, todayHistoryLoadFailure
} from './reducers';
import LivePanel from '../../components/live-panel';
import style from './style.scss';
import { NoData } from '../../components/no-data';
import ButtonGroup from '../../components/button-group';
import HourlyObservations from '../../components/hourly-observations';
import DailyObservations from '../../components/daily-observations';
import StaleDataMessage from '../../components/stale-data-message';
import { EVENT_ACTION, EVENT_CATEGORY } from '../../utils/tracking';
import PageTitle from '../../components/page-title';
import SunMoonInfo from '../../components/sun-moon-info';
import TodayInPreviousYears from '../../components/today-in-previous-years';
import { getDateTimeFormatConverter } from '../../utils/formatters';

class LivePage extends Component {

	constructor(props, context) {
		super(props, context);

		this.observationsOptions = [
			{ value: OBSERVATIONS_VIEW.HOURLY, label: '24 hrs' },
			{ value: OBSERVATIONS_VIEW.DAILY, label: '30 days' }
		];

		this.state = initialState();

		this.liveUpdateFrequency = 5000;
		this.liveUpdatePeriod = 600000;
		this.liveChartPeriod = 240000; // ms

		this.hourlyObservationsOutdated = false;
		this.dailyObservationsOutdated = false;

		this.lastUpdateTimestamp = null;
		this.units = context.userSettings.units;

		// store references to timers scheduling tasks in the future, so we can clear
		// and pending tasks when the component is unmounted.
		this.liveDataTimer = null;
		this.liveDataId = null;
		this.hourlyObservationsTimer = null;
		this.dailyObservationsTimer = null;
		this.yesterdayExtremesTimer = null;

		this.startLiveUpdateTimer = this.startLiveUpdateTimer.bind(this);
		this.fetchLiveData = this.fetchLiveData.bind(this);
		this.fetchTodayHistoryData = this.fetchTodayHistoryData.bind(this);
		this.fetchYesterdayExtremes = this.fetchYesterdayExtremes.bind(this);
		this.fetchHourlyObservations = this.fetchHourlyObservations.bind(this);
		this.fetchDailyObservations = this.fetchDailyObservations.bind(this);
		this.switchObservationsView = this.switchObservationsView.bind(this);

		this.requestAbort = {
			live: null,
			hourlyObs: null,
			dailyObs: null,
			yesterday: null,
			todayHistory: null
		};
	}

	componentDidMount() {
		this.startLiveUpdateTimer(false);
		this.fetchYesterdayExtremes();
		this.fetchTodayHistoryData();

		if (this.state.activeObservationsView === OBSERVATIONS_VIEW.HOURLY) {
			this.fetchHourlyObservations();
		} else if (this.state.activeObservationsView === OBSERVATIONS_VIEW.DAILY) {
			this.fetchDailyObservations();
		}

		this.context.titleService.update('Live');
		this.context.tracking.recordPageView();

		let hidden, visibilityChange;
		if (typeof document.hidden !== 'undefined') {
			hidden = 'hidden';
			visibilityChange = 'visibilitychange';
		} else if (typeof document.msHidden !== 'undefined') {
			hidden = 'msHidden';
			visibilityChange = 'msvisibilitychange';
		} else if (typeof document.webkitHidden !== 'undefined') {
			hidden = 'webkitHidden';
			visibilityChange = 'webkitvisibilitychange';
		}

		this.visibilityChangeHandler = () => {
			if (document[hidden]) {
				this.stopLiveUpdateTimer();
			} else {
				this.startLiveUpdateTimer(false);
			}
		};

		document.addEventListener(visibilityChange, this.visibilityChangeHandler);
	}

	componentWillReceiveProps(nextProps) {
		// If the user goes offline, stop live updates polling.
		// If the user comes online again, restart the live updates polling.
		if (nextProps.isOnline !== this.props.isOnline) {
			if (nextProps.isOnline) {
				this.startLiveUpdateTimer(false);
			} else {
				this.stopLiveUpdateTimer();
			}
		}
	}

	componentWillUnmount() {
		this.stopLiveUpdateTimer();

		if (this.hourlyObservationsTimer !== null) {
			clearTimeout(this.hourlyObservationsTimer);
		}

		if (this.dailyObservationsTimer !== null) {
			clearTimeout(this.dailyObservationsTimer);
		}

		if (this.yesterdayExtremesTimer !== null) {
			clearTimeout(this.yesterdayExtremesTimer);
		}

		// Abort any pending requests
		abortRequests(this.requestAbort);

		document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
	}

	startLiveUpdateTimer(recordEvent = true) {

		if (recordEvent) {
			// User has restarted updates
			this.context.tracking.recordEvent(EVENT_ACTION.LIVE_RESTART_UPDATES, EVENT_CATEGORY.LIVE);
		}

		// In case this somehow gets double-triggered, clear the existing timeout
		this.stopLiveUpdateTimer();

		this.setState(resumeLiveUpdates());

		this.liveDataStartTime = (new Date()).getTime();

		if (this.context.userSettings.ui.liveUpdatesEnabled) {
			this.liveDataTimer = setInterval(() => {

				this.fetchLiveData().then(() => {
					// If the next update would go beyond the live data period, pause the updates
					if (new Date().getTime() + this.liveUpdateFrequency - this.liveDataStartTime > this.liveUpdatePeriod) {

						this.setState(pauseLiveUpdates());
						this.stopLiveUpdateTimer();

					}
				});

			}, this.liveUpdateFrequency);
		}

		// Fire off the first update immediately
		this.fetchLiveData();
	}

	stopLiveUpdateTimer() {
		if (this.liveDataTimer !== null) {
			clearInterval(this.liveDataTimer);
			this.liveDataTimer = null;
		}
	}

	switchObservationsView(view) {
		this.setState(switchObservationsView(view));

		this.context.tracking.recordEvent(
			EVENT_ACTION.LIVE_TOGGLE_OBS_VIEW.replace('%1', view),
			EVENT_CATEGORY.LIVE
		);

		if (view === OBSERVATIONS_VIEW.HOURLY) {

			// Do a fetch if the obs are marked as outdated explicity, or if the data is not currently present
			// and is not in the process of loading.
			const doFetch = this.hourlyObservationsOutdated ||
				(!this.state.hourlyObservations && !this.state.hourlyObservationsLoading);

			if (doFetch) {
				this.fetchHourlyObservations();
			}

		} else if (view === OBSERVATIONS_VIEW.DAILY) {

			// Do a fetch if the obs are marked as outdated explicity, or if the data is not currently present
			// and is not in the process of loading.
			const doFetch = this.dailyObservationsOutdated ||
				(!this.state.dailyObservations && !this.state.dailyObservationsLoading);

			if (doFetch) {
				this.fetchDailyObservations();
			}

		}
	}

	fetchLiveData() {
		this.setState(liveDataLoading());

		const params = {};

		if (this.liveDataId !== null) {
			params.since = this.liveDataId;
		}

		const request = fetchLiveData(params);
		this.requestAbort.live = request.abort;

		return request.promise.then(res => {
			if (res.data.unchanged === true) {
				// Data has not changed since the previous update
				this.setState(liveDataLoadSuccess(null));
			} else {
				this.liveDataSuccess(res.data);
			}

		}).catch((err) => {
			// ignore abort error
			if (err.name !== ABORT_ERROR_NAME) {
				this.setState(state => liveDataLoadFailure(state));
				this.context.tracking.recordException('Error loading live data', err);
			}
		});

	}

	liveDataSuccess(data) {
		const timestamp = `${data.data.date} ${data.data.time}`;

		this.liveDataId = data.id;
		this.setState(liveDataLoadSuccess(data));

		if (this.lastUpdateTimestamp !== null) {

			const dateConverter = getDateTimeFormatConverter('%Y-%m-%d %H:%M:%S', '%Q');

			const d1 = new Date(Number(dateConverter(this.lastUpdateTimestamp)));
			const d2 = new Date(Number(dateConverter(timestamp)));

			// Check if the time has crossed an hour mark
			if (d1.toDateString() !== d2.toDateString() || d1.getHours() < d2.getHours()) {

				const exactHr = new Date(Number(dateConverter(timestamp)));
				exactHr.setMinutes(0);
				exactHr.setSeconds(0);

				// max timeout of 5 seconds before reloading the hourly obs.
				const diff = d2.getTime() - exactHr.getTime();
				const timeout = Math.max(0, 5000 - diff);

				// Trigger a reload of hourly observations if that is the active observations view,
				// otherwise mark the hourly observations as outdated.
				if (this.state.activeObservationsView === OBSERVATIONS_VIEW.HOURLY) {
					this.hourlyObservationsTimer = setTimeout(() => this.fetchHourlyObservations(), timeout);
				} else {
					this.hourlyObservationsOutdated = true;
				}

			}

			// Check if the time has crossed a midnight mark into a new day
			if (d1.toDateString() !== d2.toDateString()) {

				const exactDay = new Date(Number(dateConverter(timestamp)));
				exactDay.setHours(0);
				exactDay.setMinutes(0);
				exactDay.setSeconds(0);

				// max timeout of 5 seconds before reloading the daily obs / yesterday extremes.
				const diff = d2.getTime() - exactDay.getTime();
				const timeout = Math.max(0, 5000 - diff);

				// Trigger reload of yesterday's extremes after a short period (allow time for DB to update)
				// And fetch the today history data again.
				this.yesterdayExtremesTimer = setTimeout(() => {
					this.fetchYesterdayExtremes();
					this.fetchTodayHistoryData();
				}, timeout);

				// Trigger a reload of daily observations if that is the active observations view,
				// otherwise mark the daily observations as outdated.
				if (this.state.activeObservationsView === OBSERVATIONS_VIEW.DAILY) {
					this.dailyObservationsTimer = setTimeout(() => this.fetchDailyObservations(), timeout);
				} else {
					this.dailyObservationsOutdated = true;
				}

			}

		}

		this.lastUpdateTimestamp = timestamp;
	}

	fetchTodayHistoryData() {
		this.setState(todayHistoryLoading());

		const request = fetchTodayHistory();
		this.requestAbort.todayHistory = request.abort;

		request.promise
			.then(res => {
				this.setState(todayHistoryLoadSuccess(res.data));
			})
			.catch(err => {
				if (err.name !== ABORT_ERROR_NAME) {
					this.setState(state => todayHistoryLoadFailure(state));
					this.context.tracking.recordException('Error loading today history data', err);
				}
			});

	}

	fetchHourlyObservations() {
		this.setState(hourlyObservationsLoading());

		const params = {
			range: '24h',
			parameters: [
				'temperature',
				'hourlyRainfall',
				'windBearing',
				'hourlyHighWindGust'
			],
			excludeDayEnd: 1
		};

		const request = fetchObservationsHourly(params);
		this.requestAbort.hourlyObs = request.abort;

		request.promise
			.then(res => {
				this.hourlyObservationsOutdated = false;
				this.setState(hourlyObservationsLoadSuccess(res.data.observations));
			})
			.catch(err => {
				if (err.name !== ABORT_ERROR_NAME) {
					this.setState(state => hourlyObservationsLoadFailure(state));
					this.context.tracking.recordException('Error loading recent hourly observations', err);
				}
			});
	}

	fetchDailyObservations() {
		this.setState(dailyObservationsLoading());

		const params = {
			range: '30d',
			parameters: [
				'highTemperature',
				'lowTemperature',
				'avgTemperature',
				'rainfall',
				'highWindGust',
				'highWindGustBearing'
			]
		};

		const request = fetchDailyExtremes(params);
		this.requestAbort.dailyObs = request.abort;

		request.promise
			.then(res => {
				this.dailyObservationsOutdated = false;
				this.setState(dailyObservationsLoadSuccess(res.data));
			})
			.catch(err => {
				if (err.name !== ABORT_ERROR_NAME) {
					this.setState(state => dailyObservationsLoadFailure(state));
					this.context.tracking.recordException('Error loading recent daily observations', err);
				}
			});
	}

	fetchYesterdayExtremes() {
		this.setState(yesterdayExtremesLoading());

		const request = fetchDailyExtremes({ date: 'latest' });
		this.requestAbort.yesterday = request.abort;

		request.promise
			.then(res => {
				this.setState(yesterdayExtremesLoadSuccess(res.data));
			})
			.catch(err => {
				if (err.name !== ABORT_ERROR_NAME) {
					this.setState(state => yesterdayExtremesLoadFailure(state));
					this.context.tracking.recordException('Error loading yesterday extremes', err);
				}
			});
	}

	renderHourlyObservations() {
		const isListAlwaysExpanded = this.context.userSettings.ui.alwaysExpandRecentObsList;

		return (
			<HourlyObservations data={this.state.hourlyObservations}
			                    listDescending
			                    listAlwaysExpanded={isListAlwaysExpanded}
			                    isLoading={this.state.hourlyObservationsLoading}
			                    retryCount={this.state.hourlyObservationsRetryCount}
			                    retryHandler={this.fetchHourlyObservations}
			                    errorMessage="Unable to load hourly observations" />
		);
	}

	renderDailyObservations() {
		const isListAlwaysExpanded = this.context.userSettings.ui.alwaysExpandRecentObsList;

		return (
			<DailyObservations data={this.state.dailyObservations}
			                   listDescending
			                   listAlwaysExpanded={isListAlwaysExpanded}
			                   isLoading={this.state.dailyObservationsLoading}
			                   retryCount={this.state.dailyObservationsRetryCount}
			                   retryHandler={this.fetchDailyObservations}
			                   errorMessage="Unable to load daily observations" />
		);
	}

	renderStaleDataMessage() {
		if (this.state.liveData && this.state.freshness > 3600) {
			const timestamp = `${this.state.liveData.date} ${this.state.liveData.time}`;
			return (
				<StaleDataMessage freshness={this.state.freshness} timestamp={timestamp} />
			);
		}

		return null;
	}

	renderLivePanel() {
		return (
			<LivePanel data={this.state.liveData}
			           isLoading={this.state.liveDataLoading}
			           retryCount={this.state.liveDataRetryCount}
			           retryHandler={this.fetchLiveData}
			           errorMessage="Unable to load live data"
			           wrapLoadingErrorInCard />
		);
	}

	render(props) {
		const data = this.state.liveData;

		const todaysExtremes = data
			? extractDailyExtremes(data)
			: data;

		const isNoYesterdayExtremesData = this.state.yesterdayExtremes && this.state.yesterdayExtremes.length === 0;

		const yesterdayExtremes = this.state.yesterdayExtremes
			? this.state.yesterdayExtremes[0]
			: this.state.yesterdayExtremes;

		const sunMoonInfo = data
			? { ...data.sunMoon, date: data.date }
			: data;

		return (
			<div>
				<PageTitle>Live Weather Conditions</PageTitle>

				<LiveUpdatedTime date={data ? data.date : null}
				                 time={data ? data.time : null}
				                 isPaused={this.state.liveUpdatesPaused}
				                 resume={this.startLiveUpdateTimer}
				                 lastUpdateStatus={this.state.lastUpdateStatus}
				                 liveDataLoadTime={this.state.liveDataLoadTime} />

				<div class={style.conditions_status}>
					Conditions: { data ? data.conditions : '---' }
				</div>

				{ this.renderStaleDataMessage() }

				{ this.renderLivePanel() }

				<Card cardTitle="Recent Observations">
					<div class={style.recent_obs}>
						<div class={style.recent_obs_btns}>
							<ButtonGroup options={this.observationsOptions}
							             activeOption={this.state.activeObservationsView}
							             onBtnClick={this.switchObservationsView} />
						</div>
						{
							this.state.activeObservationsView === OBSERVATIONS_VIEW.HOURLY
								? this.renderHourlyObservations()
								: this.renderDailyObservations()
						}
					</div>
				</Card>

				<div class={style.secondary_layout}>

					<div class={style.secondary_box}>
						<Card cardTitle="Today's Extremes">
							<DailySummary data={todaysExtremes}
							              isLoading={this.state.liveDataLoading}
							              errorMessage="Unable to load today's extremes data"
							              retryHandler={this.fetchLiveData}
							              retryCount={this.state.liveDataRetryCount} />
						</Card>
					</div>

					<div class={style.secondary_box}>
						<Card cardTitle="Yesterday's Extremes">
							{
								isNoYesterdayExtremesData
									? <NoData />
									: <DailySummary data={yesterdayExtremes}
									                isLoading={this.state.yesterdayExtremesLoading}
									                errorMessage="Unable to load yesterday's extremes data"
									                retryHandler={this.fetchYesterdayExtremes}
									                retryCount={this.state.yesterdayExtremesRetryCount} />
							}
						</Card>
					</div>

				</div>

				<div class={[style.secondary_layout, style.final_layout].join(' ')}>
					<div class={style.secondary_box}>
						<Card cardTitle="Today in Previous Years">
							<TodayInPreviousYears data={this.state.todayHistory}
							                      isLoading={this.state.todayHistoryLoading}
							                      retryCount={this.state.todayHistoryRetryCount}
							                      retryHandler={this.fetchTodayHistoryData}
							                      errorMessage="Unable to load today history data" />
						</Card>
					</div>

					<div class={style.secondary_box}>
						<Card cardTitle="Sun and Moon">
							<SunMoonInfo data={sunMoonInfo}
							             isLoading={this.state.liveDataLoading}
							             retryCount={this.state.liveDataRetryCount}
							             retryHandler={this.fetchLiveData}
							             errorMessage="Unable to load sun/moon info" />
						</Card>
					</div>
				</div>
			</div>
		);
	}

}

export default LivePage;



// WEBPACK FOOTER //
// ./routes/live/index.js